import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import type { FilterField, FilterValues } from './types';
import { UI_STRINGS } from './locale';

/**
 * Radix Select запрещает SelectItem с пустой строкой (пустая строка = очистка
 * выбора). Для опции «Все» используем sentinel и маппим его в undefined.
 */
const ALL_VALUE = '__all__';

interface RelationOption {
  id: number;
  [key: string]: string | number | boolean | null;
}

interface RelationFilterFieldProps {
  field: Extract<FilterField, { type: 'relation' }>;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

function RelationFilterField({ field, value, onChange }: RelationFilterFieldProps) {
  const { result, query } = useList<RelationOption>({
    resource: field.resource,
    pagination: { pageSize: 200 },
    queryOptions: { staleTime: 60_000 },
  });
  const isLoading = query.isLoading;

  const options = result.data;

  return (
    <Select
      value={value ?? ALL_VALUE}
      onValueChange={(v) => onChange(v === ALL_VALUE ? undefined : v)}
    >
      <SelectTrigger className="w-full">
        <SelectValue
          placeholder={isLoading ? UI_STRINGS.loading : UI_STRINGS.selectPlaceholder(field.label.toLowerCase())}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_VALUE}>{UI_STRINGS.all}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={String(opt.id)}>
            {String(opt[field.labelField] ?? opt.id)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface UniversalFiltersProps {
  fields: FilterField[];
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  onReset: () => void;
  /** Метка кнопки фильтра */
  label?: string;
  /** Заголовок диалога */
  title?: string;
}

/**
 * Конфигурируемый компонент фильтров для таблиц.
 * Поддерживает: enum, dateRange, numberRange, relation, boolean, text.
 * Показывает количество активных фильтров в виде бейджа.
 */
export function UniversalFilters({
  fields,
  values,
  onChange,
  onReset,
  label = UI_STRINGS.filters(0),
  title = UI_STRINGS.filters(0),
}: UniversalFiltersProps) {
  const [open, setOpen] = useState(false);
  // Draft-значения в панели до нажатия "Применить"
  const [localValues, setLocalValues] = useState<FilterValues>(values);

  const activeCount = Object.values(values).filter(
    (v) => v !== undefined && v !== null && v !== '',
  ).length;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // При открытии — синхронизируемся с внешним value.
      setLocalValues(values);
    }
    setOpen(next);
  };

  const handleApply = () => {
    onChange(localValues);
    setOpen(false);
  };

  const handleReset = () => {
    setLocalValues({});
    onReset();
  };

  const setField = (key: string, value: FilterValues[string]) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Filter className="h-4 w-4" />
          <span>{label}</span>
          {activeCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary">
              {activeCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="sm:max-w-md w-full p-0 flex flex-col gap-0"
        title={title}
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {fields.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label>{field.label}</Label>
              <FilterFieldInput
                field={field}
                value={localValues[field.key]}
                onChange={(v) => setField(field.key, v)}
              />
            </div>
          ))}
        </div>

        <SheetFooter className="px-6 py-4 border-t flex-row gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={handleReset}>
            {UI_STRINGS.reset}
          </Button>
          <div className="flex gap-2">
            <SheetClose asChild>
              <Button variant="outline">{UI_STRINGS.cancel}</Button>
            </SheetClose>
            <Button onClick={handleApply}>{UI_STRINGS.apply}</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface FilterFieldInputProps {
  field: FilterField;
  value: FilterValues[string];
  onChange: (value: FilterValues[string]) => void;
}

function FilterFieldInput({ field, value, onChange }: FilterFieldInputProps) {
  switch (field.type) {
    case 'enum': {
      const strValue = typeof value === 'string' ? value : undefined;
      return (
        <Select
          value={strValue ?? ALL_VALUE}
          onValueChange={(v) => onChange(v === ALL_VALUE ? undefined : v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={UI_STRINGS.all} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>{UI_STRINGS.all}</SelectItem>
            {field.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    case 'dateRange': {
      const rangeValue = value instanceof Object && !Array.isArray(value) && typeof value !== 'boolean'
        ? (value as { from?: string; to?: string })
        : undefined;
      const dateRange = rangeValue
        ? {
          from: rangeValue.from ? new Date(rangeValue.from) : undefined,
          to: rangeValue.to ? new Date(rangeValue.to) : undefined,
        }
        : null;

      return (
        <DateRangePicker
          value={dateRange}
          onChange={(range) => {
            if (!range) {
              onChange(undefined);
              return;
            }
            onChange({
              from: range.from?.toISOString(),
              to: range.to?.toISOString(),
            });
          }}
        />
      );
    }

    case 'numberRange': {
      const rangeValue = value instanceof Object && !Array.isArray(value) && typeof value !== 'boolean'
        ? (value as { from?: string; to?: string })
        : { from: undefined, to: undefined };

      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={UI_STRINGS.from}
            value={rangeValue.from ?? ''}
            onChange={(e) =>
              onChange({ ...(rangeValue), from: e.target.value || undefined })}
            className="flex-1"
          />
          <span className="text-muted-foreground text-sm shrink-0">—</span>
          <Input
            type="number"
            placeholder={UI_STRINGS.to}
            value={rangeValue.to ?? ''}
            onChange={(e) =>
              onChange({ ...(rangeValue), to: e.target.value || undefined })}
            className="flex-1"
          />
        </div>
      );
    }

    case 'relation': {
      const strValue = typeof value === 'string' ? value : undefined;
      return (
        <RelationFilterField
          field={field}
          value={strValue}
          onChange={(v) => onChange(v)}
        />
      );
    }

    case 'boolean': {
      const boolValue = typeof value === 'boolean' ? value : false;
      const isActive = value !== undefined && value !== null && value !== '';
      return (
        <div className="flex items-center gap-4">
          <Switch
            checked={boolValue}
            onCheckedChange={(checked) => onChange(checked)}
          />
          {isActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-muted-foreground"
              onClick={() => onChange(undefined)}
            >
              {UI_STRINGS.clear}
            </Button>
          )}
        </div>
      );
    }

    case 'text': {
      const strValue = typeof value === 'string' ? value : '';
      return (
        <Input
          placeholder={UI_STRINGS.searchByField(field.label.toLowerCase())}
          value={strValue}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      );
    }
  }
}
